/**
 * Theme Detector — samples the background luminance behind a target element
 * to determine whether dark-glass or light-glass tokens should be used.
 *
 * Uses a small offscreen canvas to read averaged pixel color from the area
 * underneath the widget, then derives perceived luminance.
 */

export type GlassTheme = 'dark' | 'light'

/**
 * Sample the average luminance of the page background behind a given rect.
 * Returns 'dark' if the background is light (use dark text / light glass),
 * 'light' if the background is dark (use light text / dark glass).
 *
 * Note: the naming follows the *glass* convention — "dark glass" appears on
 * dark backgrounds, "light glass" appears on light backgrounds.
 */
export function detectTheme(rect: DOMRect): GlassTheme {
  try {
    // Sample the center point's computed background color up the DOM tree
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const el = document.elementFromPoint(centerX, centerY)

    if (!el) return 'dark'

    const bgColor = getEffectiveBackgroundColor(el as HTMLElement)
    const luminance = relativeLuminance(bgColor)

    // Light background → light glass (translucent white, dark text)
    // Dark background → dark glass (translucent white on dark, light text)
    return luminance > 0.5 ? 'light' : 'dark'
  } catch {
    return 'dark' // safe default
  }
}

/**
 * Walk up the DOM tree to find the first element with a non-transparent background.
 * Returns the color as [r, g, b].
 */
function getEffectiveBackgroundColor(el: HTMLElement): [number, number, number] {
  let current: HTMLElement | null = el

  while (current) {
    const style = getComputedStyle(current)
    const bg = style.backgroundColor

    if (bg && bg !== 'transparent' && bg !== 'rgba(0, 0, 0, 0)') {
      return parseColor(bg)
    }

    current = current.parentElement
  }

  // Fallback: assume white page
  return [255, 255, 255]
}

/** Parse a CSS color string (rgb/rgba) into [r, g, b]. */
function parseColor(color: string): [number, number, number] {
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (match) {
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])]
  }
  return [255, 255, 255] // fallback
}

/**
 * Compute relative luminance (0 = black, 1 = white) per WCAG 2.0.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4),
  )
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Apply the detected theme as a data attribute on an element.
 * All child glass components will pick up the themed CSS variables.
 */
export function applyTheme(container: HTMLElement, theme: GlassTheme): void {
  container.setAttribute('data-hs-theme', theme)
}

/**
 * Auto-detect and apply theme. Call this on init and optionally on scroll/resize.
 * Returns the detected theme.
 */
export function autoDetectAndApply(container: HTMLElement, referenceRect?: DOMRect): GlassTheme {
  const rect = referenceRect ?? container.getBoundingClientRect()
  const theme = detectTheme(rect)
  applyTheme(container, theme)
  return theme
}
